import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import React from "react";
import { useSearchParams } from "react-router-dom";

import * as api from "@/api/orders";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { AnalyticsTab } from "./AnalyticsTab";
import { ControlTab } from "./ControlTab";
import { fmtDate, timeAgo } from "./format";
import { MatrixTab } from "./MatrixTab";
import type { Tab } from "./metrics";
import { PlanTab } from "./PlanTab";
import { ProductionTab } from "./ProductionTab";
import { SettingsTab } from "./settings/SettingsTab";

type PageTab = Tab | "plan" | "production" | "control" | "analytics" | "settings";

const TABS: { value: PageTab; label: string }[] = [
  { value: "all", label: "Свод" },
  { value: "wb", label: "Wildberries" },
  { value: "ozon", label: "Ozon" },
  { value: "ym", label: "Яндекс Маркет" },
  { value: "plan", label: "Прогноз" },
  { value: "production", label: "Производство" },
  { value: "control", label: "Контроль" },
  { value: "analytics", label: "Аналитика" },
  { value: "settings", label: "Настройки" },
];

export function OrdersPage(): React.ReactElement {
  // Вкладка и период — в URL (deep-link для скриншот-харнесса devtools/shoot.mjs)
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") ?? "all") as PageTab;
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  // Черновик периода применяется кнопкой: каждое изменение = пересборка на сервере
  const [draftFrom, setDraftFrom] = React.useState(from);
  const [draftTo, setDraftTo] = React.useState(to);

  const setParam = (key: string, value: string): void => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const query = useQuery({
    queryKey: ["orders", "matrix", from, to],
    queryFn: () =>
      api.getMatrix({
        ...(from ? { date_from: from } : {}),
        ...(to ? { date_to: to } : {}),
      }),
    staleTime: 5 * 60 * 1000,
    retry: false, // 503 «источник не настроен» ретраями не лечится
  });

  const applyPeriod = (): void => {
    setParam("from", draftFrom);
    setParam("to", draftTo);
  };
  const periodDirty = draftFrom !== from || draftTo !== to;

  return (
    <div className="mx-auto max-w-none space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Заказы</h1>
        <div className="ml-auto flex items-center gap-2">
          <DateRangeFilter
            dateFrom={draftFrom}
            dateTo={draftTo}
            placeholder="Период (по умолчанию с 2025)"
            onChange={(f, t) => {
              setDraftFrom(f);
              setDraftTo(t);
            }}
          />
          <Button size="sm" disabled={!periodDirty} onClick={applyPeriod}>
            Применить
          </Button>
        </div>
      </div>

      {query.data && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>
            Данные: {fmtDate(query.data.date_from)} — {fmtDate(query.data.date_to)}
            {" · "}заказы до {fmtDate(query.data.as_of)}
          </span>
          <span>
            собрано {timeAgo(query.data.generated_at)}
            {query.data.from_cache ? " (из кеша)" : ""}
          </span>
          <span>
            строк источника: {query.data.meta.source_rows.toLocaleString("ru-RU")}
          </span>
          {query.data.warnings.map((w) => (
            <span key={w} className="text-amber-600 dark:text-amber-400">{w}</span>
          ))}
        </div>
      )}

      <div className="flex gap-4 border-b">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setParam("tab", t.value === "all" ? "" : t.value)}
            className={cn(
              "-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors",
              tab === t.value
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {query.isLoading && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
          <RefreshCw className="size-4 animate-spin" />
          Собираю матрицу заказов… При холодном кеше (первый запрос после обновления данных)
          это занимает до минуты — дальше мгновенно.
        </div>
      )}
      {query.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
          Не удалось получить данные: {extractErrorMessage(query.error, "ошибка запроса")}
        </div>
      )}

      {query.data && !["plan", "production", "control", "analytics", "settings"].includes(tab) && (
        <MatrixTab report={query.data} tab={tab as Tab} />
      )}
      {query.data && tab === "plan" && <PlanTab report={query.data} />}
      {query.data && tab === "production" && <ProductionTab report={query.data} />}
      {query.data && tab === "control" && <ControlTab report={query.data} />}
      {query.data && tab === "analytics" && <AnalyticsTab report={query.data} />}
      {tab === "settings" && <SettingsTab report={query.data} />}
    </div>
  );
}
